import { Api } from 'api';
import { dateToFormat } from 'helpers/formatters/date';
import { CellStatus } from '../../../../components/Table/helpers';
import { JobHistory } from '../types/JobHistory';
import { DownloadDiagnostics } from 'components/Table/helpers/DownloadDiagnostics/DownloadDiagnostics';
import { useMemo, useState } from 'react';
import {
  FilterPlaceholders,
  SortByDateTypes,
  SortByNumberOptions,
  SortByTextOptions,
  SortType,
  SortbyDateOptions,
  doFilterData,
  doSort,
} from 'components/Table/helpers/tableColumnSort';
import {
  FilterSelect,
  SelectColumnFilter,
} from 'components/Table/helpers/SelectColumnFilter/SelectColumnFilter';

const getDate = (value: Date, format: string) => {
  return value ? dateToFormat(new Date(value), format) : '-';
};

interface Props {
  searchInput?: string;
}

export const useTable = ({ searchInput }: Props) => {
  const { data: jobHistory, isLoading } = Api.jobHistory.GetJobHistories();
  const [sort, setSort] = useState<any>({
    runStartDate: {
      value: SortByDateTypes.MostRecent,
      label: SortByDateTypes.MostRecent,
      type: SortType.Date,
    },
  });
  const [filter, setFilter] = useState<any>({});
  const initData = jobHistory?.data
    ? jobHistory.data.map((item: JobHistory) => {
        return {
          id: item.id,
          status: item.status.name,
          runEndDateFormatted: getDate(item.runEndDate, 'MM/dd/y; h:mm a'),
          runEndDate: item.runEndDate,
          runStartDateFormatted: getDate(item.runStartDate, 'MM/dd/y; h:mm a'),
          runStartDate: item.runStartDate,
          rangeStartDate: item.rangeStartDate,
          calculationPeriod: `${getDate(
            item.rangeStartDate,
            'MMM y'
          )} - ${getDate(item.rangeEndDate, 'MMM y')}`,
          numberOfBenchmarks: item.numberOfBenchmarks,
          jobType: item.job.jobType?.name ?? '-',
          name: item.job.name || '-',
        };
      })
    : [];

  const sortByText = (key: string, value: any) => {
    setSort({ [key]: { ...value, type: SortType.Text } });
  };

  const sortByNumber = (key: string, value: any) => {
    setSort({ [key]: { ...value, type: SortType.Number } });
  };

  const sortByDate = (key: string, value: any) => {
    setSort({ [key]: { ...value, type: SortType.Date } });
  };

  const handleFilter = (key: string, value: any) => {
    if (value.label === FilterPlaceholders.All) {
      delete filter[key];
      setFilter({ ...filter });
    } else {
      setFilter({ ...filter, [key]: value });
    }
  };

  const tableData = useMemo(() => {
    let d = doFilterData(initData, filter);
    if (searchInput) {
      d = d.filter((item: any) => {
        return item.name.toLowerCase().indexOf(searchInput) >= 0;
      });
    }
    const key = Object.keys(sort)[0];

    return doSort(d, key, sort[key]);
  }, [initData, sort, filter, searchInput]);

  const columns = [
    {
      Header: 'Name of Job',
      accessor: 'name',
      SortedBy: () =>
        FilterSelect({
          placeholder: FilterPlaceholders.SortBy,
          options: SortByTextOptions,
          value: sort['name'],
          onChange: (props: any) => sortByText('name', props),
        }),
    },
    {
      Header: 'Occurrence',
      accessor: 'jobType',
      hasFilter: true,
      Filter: (props: any) =>
        SelectColumnFilter({
          ...props,
          placeholder: FilterPlaceholders.All,
          value: filter['jobType'],
          initData,
          onChange: (props: any) => handleFilter('jobType', props),
        }),
    },
    {
      Header: '# of Benchmarks',
      accessor: 'numberOfBenchmarks',
      SortedBy: () =>
        FilterSelect({
          placeholder: FilterPlaceholders.SortBy,
          options: SortByNumberOptions,
          value: sort['numberOfBenchmarks'],
          onChange: (props: any) => sortByNumber('numberOfBenchmarks', props),
        }),
    },
    {
      Header: 'Calculation Period',
      accessor: 'calculationPeriod',
      className: 'textCenter',
      SortedBy: () =>
        FilterSelect({
          placeholder: FilterPlaceholders.SortBy,
          options: SortbyDateOptions,
          value: sort['rangeStartDate'],
          onChange: (props: any) => sortByDate('rangeStartDate', props),
        }),
    },
    {
      Header: 'Run Start Date',
      accessor: 'runStartDateFormatted',
      className: 'textCenter',
      SortedBy: () =>
        FilterSelect({
          placeholder: FilterPlaceholders.SortBy,
          options: SortbyDateOptions,
          value: sort['runStartDate'],
          onChange: (props: any) => sortByDate('runStartDate', props),
        }),
    },
    {
      Header: 'Run End Date',
      accessor: 'runEndDateFormatted',
      className: 'textCenter',
      SortedBy: () =>
        FilterSelect({
          placeholder: FilterPlaceholders.SortBy,
          options: SortbyDateOptions,
          value: sort['runEndDate'],
          onChange: (props: any) => sortByDate('runEndDate', props),
        }),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: CellStatus('status'),
      hasFilter: true,
      Filter: (props: any) =>
        SelectColumnFilter({
          ...props,
          placeholder: FilterPlaceholders.All,
          value: filter['status'],
          initData,
          onChange: (props: any) => handleFilter('status', props),
        }),
    },
    {
      Header: 'Actions',
      accessor: 'id',
      Cell: DownloadDiagnostics(),
    },
  ];

  const table = {
    data: tableData,
    columns,
  };

  return { table, isLoading };
};
